import { HTTPError, TimeoutError } from 'ky';
import { TypeOf, ZodTypeAny } from 'zod';
import { createFetcher, Fetcher } from './api-client.ts';
import { ApiFlexError } from '@/services/api/api-error.ts';
import { useAuthStore } from '@/stores/auth.ts';

export const ERRORS = {
  ApplicationError: 'ApplicationError',
  ApiError: 'ApiError',
  ServerError: 'ServerError',
  TimeoutError: 'TimeoutError',
  UnexpectedError: 'UnexpectedError',
} as const;

export type ErrorCategory = keyof typeof ERRORS;

/** UI에서 에러를 값으로 처리 */
export interface ErrorAsValue {
  category: ErrorCategory;
  name: string;
  message: string;
  resultCode?: number;
  host?: string;
  timestamp?: number;
  url?: string;
}

export function isErrorValue(obj: unknown): obj is ErrorAsValue {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'category' in obj &&
    'name' in obj
  );
}

/**
 * API 요청 서비스의 기본 클래스
 * 타입 정의는 이 소스 하단부 참고
 */
export abstract class BaseApi {
  /** ky 인스턴스 */
  private readonly fetcher: Fetcher;

  protected constructor() {
    // TODO: 생성시 주입
    this.fetcher = createFetcher();
  }

  /**
   * API 요청 함수. Zod 스키마로 API 응답값을 검증 후 파싱하여 타입과 함께 리턴.
   */
  async requestAsync<SCHEMA extends ZodTypeAny, PAYLOAD>(
    options: RequestOptions<SCHEMA, PAYLOAD>
  ): Promise<TypeOf<SCHEMA>> {
    try {
      const auth = useAuthStore();
      const response = await this.fetcher(options.url, {
        method: options.method || 'get',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${auth.token}`,
        },
        searchParams: this.toQueryString(options.query),
        json: options.payload,
      });

      if (response.status >= 500)
        throw new ApiFlexError({
          category: 'ServerError',
          name: 'RequestFailed',
          url: response.url,
          message: `${response.status} (${response.statusText})`,
        });

      const token = response.headers.get('authorization');
      if (token) auth.setToken(token);

      const result = (await response.json()) as ApiResponse<unknown>;

      if (result.error)
        throw new ApiFlexError({
          category: 'ApiError',
          url: response.url,
          resultCode: result.resultCode,
          ...result.error,
        });

      if (result.value) {
        // value 값이 있으면 스키마 검증
        const validated = options.schema.safeParse(result.value);

        if (!validated.success) {
          const validationErrors = validated.error.issues;
          console.group(
            '%cFailed to validate API response',
            'padding: 4px; background-color: red; color: #fff;'
          );
          console.info(validationErrors);
          console.groupEnd();

          // 프론트엔드에 정의된 스키마와 API의 응답 형식이 일치하지 않는 경우
          throw new ApiFlexError({
            category: 'ApiError',
            name: 'ValidationFailed',
            message: JSON.stringify(validationErrors, null, 2),
            resultCode: result.resultCode,
            url: response.url,
          });
        }
        return validated.data as TypeOf<SCHEMA>;
      }

      if (result.resultCode === 0) {
        // result, value가 없고 resultCode가 0이라면 API 요청은 성공
        return undefined;
      }

      throw new ApiFlexError({
        category: 'ApiError',
        name: 'InvalidResponse',
        message: 'API 응답에 value 또는 error 값이 없습니다.',
        resultCode: result.resultCode,
        url: response.url,
      });
    } catch (err: unknown) {
      if (err instanceof ApiFlexError) throw err;

      // 서버가 응답한 경우
      if (err instanceof HTTPError) {
        const errorJson = (await err.response.json()) as ApiError;
        throw new ApiFlexError({
          category: 'ApiError',
          ...errorJson,
          url: err.response.url,
        });
      }

      // Ky 에서 타임아웃 에러가 났을 경우
      if (err instanceof TimeoutError) {
        throw new ApiFlexError({
          category: 'TimeoutError',
          name: 'RequestTimeout',
          message:
            'API 응답이 오래 걸리고 있습니다. 네트워크 또는 백엔드 서버의 상태를 체크해주세요.',
          url: err.request.url,
        });
      }

      // 여기서부터는 코드가 잘못되서 에러
      console.group(
        '%cAn error occurred while communicating with the API',
        'padding: 4px; background-color: rebeccapurple; color: #fff;'
      );
      console.error(err);
      console.groupEnd();

      if (err instanceof Error) {
        throw new ApiFlexError({
          category: 'ApplicationError',
          name: err.name,
          message: err.message,
        });
      }

      throw new ApiFlexError({
        category: 'UnexpectedError',
        name: 'Unknown',
        message:
          '정의되지 않은 에러가 발생하였습니다. 자세한 사항은 브라우저 콘솔에서 확인하세요.',
      });
    }
  }

  private toQueryString(obj?: unknown) {
    if (!obj) return '';

    try {
      const qs = new URLSearchParams(obj as any);
      return qs.toString();
    } catch {
      return '';
    }
  }
}

/** API 에러 응답 */
interface ApiError {
  name: string;
  message: string;
  host: string;
  timestamp: number;
}

/** API의 기본 응답 */
interface ApiResponse<VALUE> {
  resultCode: number;
  value?: VALUE;
  error?: ApiError;
}

interface RequestOptions<T extends ZodTypeAny, U = {}> {
  /** API 서브 경로 */
  url: string;

  /** 생략시 get */
  method?: 'get' | 'post' | 'put' | 'delete';

  /** URL의 쿼리스트링 key: value 객체 */
  query?: Record<string, unknown>;

  /* Zod 스키마 */
  schema: T;

  /** 요청의 body */
  payload?: U;
}
